import { useRef } from 'react';
import type { CSSProperties, KeyboardEvent, MouseEvent, ReactElement, RefObject } from 'react';
import { dataEl, type DataElScope } from '@/utils/dataEl';
import { FontWeight, getFontFamily } from '@/theme/typography';
import { adjustWeightForHebrew } from '../Cmarkdown/markdown-hebrew';
import { TABLE_HEADER_SURFACE_DEFAULT, useSessionTweaks, type TableRowHoverVariant } from '../Csessionpage/sessionTweaks';
import { RTL_CELL_STYLE, TABLE_ACTIVE_ROW_BACKGROUND, TABLE_BODY_BORDER_BOTTOM, TABLE_BODY_HOVER_TEXT, TABLE_BODY_TEXT_COLOR, TABLE_GRID_ALIGN_START, TABLE_GRID_BACKGROUND_TRANSPARENT, TABLE_GRID_DISPLAY, TABLE_GRID_FULL_SPAN, TABLE_GRID_SUBGRID, TABLE_HEADER_BORDER_BOTTOM, TABLE_HEADER_SHIMMER_BG, TABLE_HEADER_SURFACE_STYLE, TABLE_ROW_HOVER_STYLE, TABLE_STYLE_VALUE, } from './Cmarkdowntable.constants';
import { resolveTableCellRenderState, shouldIgnoreRowActivation, } from './Cmarkdowntable.helpers';
import type { CmarkdowntableCell, CmarkdowntableTextAlign, OpenTablePopoverRowInput, TableRowKeyDownInput, } from './Cmarkdowntable.types';
import { ButtonPreset } from '@/constants/componentPresets';
import { Cbutton } from '../Cbutton';
const ROW_ROOT_ROLE = "row";
const MARKDOWN_TABLE_HEADER_CONTEXT = 'headerRow';
const MARKDOWN_TABLE_COLUMN_PREFIX = 'column';
type CmarkdowntableCellRenderState = ReturnType<typeof resolveTableCellRenderState>;
interface CmarkdowntableGridProps {
    dataElScope: DataElScope;
    headerCells: CmarkdowntableCell[];
    rows: CmarkdowntableCell[][];
    textAlign?: CmarkdowntableTextAlign;
    tableElementRef: RefObject<HTMLDivElement | null>;
    activeRowIndex: number | null;
    hoverRowIndex: number | null;
    onHoverRowChange: (rowIndex: number | null) => void;
    onOpenRow: (input: OpenTablePopoverRowInput) => void;
    onRowKeyDown: (input: TableRowKeyDownInput) => void;
    headerPadding: string;
    bodyPadding: string;
    headerCellContentStyle: CSSProperties;
    baseBodyCellContentStyle: CSSProperties;
    baseBodyWeight: FontWeight;
}
interface CmarkdowntableHeaderRowProps {
    headerCells: CmarkdowntableCell[];
    textAlign?: CmarkdowntableTextAlign;
    headerPadding: string;
    headerCellContentStyle: CSSProperties;
}
interface CmarkdowntableHeaderCellProps {
    cell: CmarkdowntableCell;
    textAlign?: CmarkdowntableTextAlign;
    headerPadding: string;
    headerCellContentStyle: CSSProperties;
    columnIdentity: string;
}
interface CmarkdowntableBodyRowProps {
    row: CmarkdowntableCell[];
    rowIndex: number;
    rowHoverVariant: TableRowHoverVariant;
    textAlign?: CmarkdowntableTextAlign;
    activeRowIndex: number | null;
    hoverRowIndex: number | null;
    onHoverRowChange: (rowIndex: number | null) => void;
    onOpenRow: (input: OpenTablePopoverRowInput) => void;
    onRowKeyDown: (input: TableRowKeyDownInput) => void;
    bodyPadding: string;
    baseBodyCellContentStyle: CSSProperties;
    baseBodyWeight: FontWeight;
}
interface CmarkdowntableBodyRowsProps {
    rows: CmarkdowntableCell[][];
    rowHoverVariant: TableRowHoverVariant;
    textAlign?: CmarkdowntableTextAlign;
    activeRowIndex: number | null;
    hoverRowIndex: number | null;
    onHoverRowChange: (rowIndex: number | null) => void;
    onOpenRow: (input: OpenTablePopoverRowInput) => void;
    onRowKeyDown: (input: TableRowKeyDownInput) => void;
    bodyPadding: string;
    baseBodyCellContentStyle: CSSProperties;
    baseBodyWeight: FontWeight;
}
interface CmarkdowntableBodyCellProps {
    cell: CmarkdowntableCell;
    textAlign?: CmarkdowntableTextAlign;
    isHighlighted: boolean;
    bodyPadding: string;
    baseBodyCellContentStyle: CSSProperties;
    baseBodyWeight: FontWeight;
    cellIdentity: string;
}
interface OpenCmarkdowntableBodyRowInput {
    event: MouseEvent<HTMLDivElement>;
    row: CmarkdowntableCell[];
    rowIndex: number;
    onOpenRow: (input: OpenTablePopoverRowInput) => void;
    anchorElement: HTMLDivElement | null;
}
interface HandleCmarkdowntableBodyRowKeyDownInput {
    event: KeyboardEvent<HTMLDivElement>;
    row: CmarkdowntableCell[];
    rowIndex: number;
    onRowKeyDown: (input: TableRowKeyDownInput) => void;
    anchorElement: HTMLDivElement | null;
}
interface GetCmarkdowntableBodyRowStyleInput {
    isActive: boolean;
    isHovered: boolean;
    rowHoverVariant: TableRowHoverVariant;
}
interface GetCmarkdowntableBodyCellStyleInput {
    bodyPadding: string;
    cellState: CmarkdowntableCellRenderState;
}
interface GetCmarkdowntableBodyCellContentStyleInput {
    cellState: CmarkdowntableCellRenderState;
    isHighlighted: boolean;
    baseBodyCellContentStyle: CSSProperties;
    baseBodyWeight: FontWeight;
}
export function CmarkdowntableGrid({ dataElScope, headerCells, rows, textAlign, tableElementRef, activeRowIndex, hoverRowIndex, onHoverRowChange, onOpenRow, onRowKeyDown, headerPadding, bodyPadding, headerCellContentStyle, baseBodyCellContentStyle, baseBodyWeight, }: CmarkdowntableGridProps): ReactElement {
    const { tableRowHoverVariant } = useSessionTweaks();
    const gridColumnTemplate = `repeat(${Math.max(headerCells.length, 1)}, auto)`;
    return (
      <div data-el={dataEl(dataElScope, 'Grid/Root')} ref={tableElementRef} role="table" style={{
            display: TABLE_GRID_DISPLAY,
            gridTemplateColumns: gridColumnTemplate,
            alignContent: TABLE_GRID_ALIGN_START,
            minHeight: TABLE_STYLE_VALUE.HeightFull,
            width: TABLE_STYLE_VALUE.WidthMaxContent,
            minWidth: TABLE_STYLE_VALUE.WidthFull,
        }} onMouseLeave={() => onHoverRowChange(null)}>
        <CmarkdowntableHeaderRow headerCells={headerCells} textAlign={textAlign} headerPadding={headerPadding} headerCellContentStyle={headerCellContentStyle}/>

        <CmarkdowntableBodyRows rows={rows} rowHoverVariant={tableRowHoverVariant} textAlign={textAlign} activeRowIndex={activeRowIndex} hoverRowIndex={hoverRowIndex} onHoverRowChange={onHoverRowChange} onOpenRow={onOpenRow} onRowKeyDown={onRowKeyDown} bodyPadding={bodyPadding} baseBodyCellContentStyle={baseBodyCellContentStyle} baseBodyWeight={baseBodyWeight}/>
      </div>
    );
}
function CmarkdowntableHeaderRow({ headerCells, textAlign, headerPadding, headerCellContentStyle, }: CmarkdowntableHeaderRowProps): ReactElement {
    // Header surface baked to Eclipse (locked tableHeaderSurface). Paint on the
    // sticky ROW — per-cell paint let scrolled body text bleed through.
    return (
      <div role="row" style={{
            display: TABLE_GRID_DISPLAY,
            gridColumn: TABLE_GRID_FULL_SPAN,
            gridTemplateColumns: TABLE_GRID_SUBGRID,
            position: 'sticky',
            top: 0,
            zIndex: 2,
            alignSelf: 'start',
            ...TABLE_HEADER_SURFACE_STYLE[TABLE_HEADER_SURFACE_DEFAULT],
        }}>
        {headerCells.map((cell, columnIndex) => (<CmarkdowntableHeaderCell key={`header-cell-${columnIndex}`} cell={cell} textAlign={textAlign} headerPadding={headerPadding} headerCellContentStyle={headerCellContentStyle} columnIdentity={`${MARKDOWN_TABLE_COLUMN_PREFIX}-${columnIndex}`}/>))}
      </div>
    );
}
function CmarkdowntableHeaderCell({ cell, textAlign, headerPadding, headerCellContentStyle, columnIdentity, }: CmarkdowntableHeaderCellProps): ReactElement {
    const cellState = resolveTableCellRenderState({ cell, textAlign });
    return (
      <div role="columnheader" style={{
            padding: headerPadding,
            borderBottom: TABLE_HEADER_BORDER_BOTTOM,
            textAlign: cellState.alignment,
            position: 'relative',
            ...(cellState.hebrew ? RTL_CELL_STYLE : null),
        }}>
        <div style={{
            ...headerCellContentStyle,
            ...(cellState.hebrew ? { fontWeight: adjustWeightForHebrew(FontWeight.Bold) } : null),
            ...(cellState.isNumericColumn ? { fontFamily: getFontFamily() } : null),
        }}>
          {cell.content}
        </div>
        <div aria-hidden style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: -1,
            height: 1,
            backgroundImage: TABLE_HEADER_SHIMMER_BG,
            pointerEvents: 'none',
        }}/>
      </div>
    );
}
function CmarkdowntableBodyRows({ rows, rowHoverVariant, textAlign, activeRowIndex, hoverRowIndex, onHoverRowChange, onOpenRow, onRowKeyDown, bodyPadding, baseBodyCellContentStyle, baseBodyWeight, }: CmarkdowntableBodyRowsProps): ReactElement {
    return (<>
      {rows.map((row, rowIndex) => (<CmarkdowntableBodyRow key={`body-row-${rowIndex}`} row={row} rowIndex={rowIndex} rowHoverVariant={rowHoverVariant} textAlign={textAlign} activeRowIndex={activeRowIndex} hoverRowIndex={hoverRowIndex} onHoverRowChange={onHoverRowChange} onOpenRow={onOpenRow} onRowKeyDown={onRowKeyDown} bodyPadding={bodyPadding} baseBodyCellContentStyle={baseBodyCellContentStyle} baseBodyWeight={baseBodyWeight}/>))}
    </>);
}
function CmarkdowntableBodyRow({ row, rowIndex, rowHoverVariant, textAlign, activeRowIndex, hoverRowIndex, onHoverRowChange, onOpenRow, onRowKeyDown, bodyPadding, baseBodyCellContentStyle, baseBodyWeight, }: CmarkdowntableBodyRowProps): ReactElement {
    const rowIdentity = `row-${rowIndex}`;
    // The popover anchors to THIS ref, never to the click's `currentTarget`.
    // Cbutton routes onClick to its Ccontainer root, which the row renders
    // `display: contents` so the cells stay in the subgrid — a boxless element,
    // whose getBoundingClientRect is all zeros. Anchoring there read as
    // "off-screen" and closed the popover in the same tick it opened.
    const rowElementRef = useRef<HTMLDivElement>(null);
    const isHovered = hoverRowIndex === rowIndex;
    const isActive = activeRowIndex === rowIndex;
    const isHighlighted = isHovered || isActive;
    return (
      <Cbutton preset={ButtonPreset.Ghost} rootRole={ROW_ROOT_ROLE} rootRef={rowElementRef} ariaLabel={`Open table ${rowIdentity}`} ariaPressed={isActive} padding={0} onMouseEnter={() => onHoverRowChange(rowIndex)} onClick={(event) => {
            if (event) {
                openCmarkdowntableBodyRow({ event, rowIndex, row, onOpenRow, anchorElement: rowElementRef.current });
            }
        }} onKeyDown={(event) => handleCmarkdowntableBodyRowKeyDown({ event, rowIndex, row, onRowKeyDown, anchorElement: rowElementRef.current })} style={getCmarkdowntableBodyRowStyle({ isActive, isHovered, rowHoverVariant })} containerStyle={{ display: 'contents' }} contentStyle={{ display: 'contents' }}>
        {row.map((cell, columnIndex) => (<CmarkdowntableBodyCell key={`body-cell-${rowIndex}-${columnIndex}`} cell={cell} textAlign={textAlign} isHighlighted={isHighlighted} bodyPadding={bodyPadding} baseBodyCellContentStyle={baseBodyCellContentStyle} baseBodyWeight={baseBodyWeight} cellIdentity={`row-${rowIndex}-column-${columnIndex}`}/>))}
      </Cbutton>
    );
}
function openCmarkdowntableBodyRow({ event, row, rowIndex, onOpenRow, anchorElement, }: OpenCmarkdowntableBodyRowInput): void {
    if (!anchorElement) {
        return;
    }
    if (shouldIgnoreRowActivation(event.target)) {
        return;
    }
    event.stopPropagation();
    onOpenRow({
        rowIndex,
        row,
        anchorElement,
    });
}
function handleCmarkdowntableBodyRowKeyDown({ event, row, rowIndex, onRowKeyDown, anchorElement, }: HandleCmarkdowntableBodyRowKeyDownInput): void {
    if (!anchorElement) {
        return;
    }
    onRowKeyDown({
        event,
        rowIndex,
        row,
        anchorElement,
    });
}
function getCmarkdowntableBodyRowStyle({ isActive, isHovered, rowHoverVariant, }: GetCmarkdowntableBodyRowStyleInput): CSSProperties {
    return {
        display: TABLE_GRID_DISPLAY,
        gridColumn: TABLE_GRID_FULL_SPAN,
        gridTemplateColumns: TABLE_GRID_SUBGRID,
        cursor: 'pointer',
        transition: 'background 0.12s',
        ...resolveTableBodyRowHighlightStyle({ isActive, isHovered, rowHoverVariant }),
    };
}
function resolveTableBodyRowHighlightStyle({ isActive, isHovered, rowHoverVariant, }: GetCmarkdowntableBodyRowStyleInput): CSSProperties {
    if (isActive) {
        return { backgroundColor: TABLE_ACTIVE_ROW_BACKGROUND };
    }
    if (isHovered) {
        return TABLE_ROW_HOVER_STYLE[rowHoverVariant];
    }
    return { backgroundColor: TABLE_GRID_BACKGROUND_TRANSPARENT };
}
function CmarkdowntableBodyCell({ cell, textAlign, isHighlighted, bodyPadding, baseBodyCellContentStyle, baseBodyWeight, cellIdentity, }: CmarkdowntableBodyCellProps): ReactElement {
    const cellState = resolveTableCellRenderState({ cell, textAlign });
    return (
      <div role="cell" style={getCmarkdowntableBodyCellStyle({ bodyPadding, cellState })}>
        <div style={getCmarkdowntableBodyCellContentStyle({
            cellState,
            isHighlighted,
            baseBodyCellContentStyle,
            baseBodyWeight,
        })}>
          {cell.content}
        </div>
      </div>
    );
}
function getCmarkdowntableBodyCellStyle({ bodyPadding, cellState, }: GetCmarkdowntableBodyCellStyleInput): CSSProperties {
    return {
        padding: bodyPadding,
        borderBottom: TABLE_BODY_BORDER_BOTTOM,
        textAlign: cellState.alignment,
        ...(cellState.hebrew ? RTL_CELL_STYLE : null),
    };
}
function getCmarkdowntableBodyCellContentStyle({ cellState, isHighlighted, baseBodyCellContentStyle, baseBodyWeight, }: GetCmarkdowntableBodyCellContentStyleInput): CSSProperties {
    return {
        ...baseBodyCellContentStyle,
        color: isHighlighted ? TABLE_BODY_HOVER_TEXT : TABLE_BODY_TEXT_COLOR,
        transition: 'color 0.12s',
        ...(cellState.hebrew ? { fontWeight: adjustWeightForHebrew(baseBodyWeight) } : null),
        ...(cellState.isNumericColumn ? { fontFamily: getFontFamily() } : null),
    };
}
